import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef, OverlayOutsideClickDispatcher } from '@angular/cdk/overlay';
import { fromEvent, Subscription } from 'rxjs';
import { take, filter } from 'rxjs/operators';
import { ContextMenuOption } from '../../models/context-menu-option';
@Component({
  standalone: false,
  selector: 'res-context-menu',
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss']
})
export class ContextMenuComponent {
  @ViewChild('contextMenu') contextMenu: TemplateRef<any>;
  @ViewChild('headerContextMenu') headerContextMenu: TemplateRef<any>;
  @ViewChild('collapsedContextMenu') collapsedContextMenu: TemplateRef<any>;
  @Input() contextMenuOptions: Array<ContextMenuOption>;
  @Input() onModelChangeEvent: (event?: any) => void;
  @Input() deselectAllEvent: (event?: any) => void;
  @Input() closeContextMenuEvent: (event?: any) => void;
  @Input() openMenuOnLeft: boolean;
  @Input() removeLeftMargin: boolean;
  @Input() hasDeselectAll: boolean;
  @Input() isSkeuomorphic: boolean;
  @Input() labelPaddingLeft: number;
  @Input() labelPaddingRight: number;
  @Input() isAdvancedSearch: boolean;
  contextMenuTemplate: TemplateRef<any>;
  sub: Subscription;
  overlayRef: OverlayRef | null;

  constructor(public overlay: Overlay, public viewContainerRef: ViewContainerRef, public overlayOutsideClickDispatcher: OverlayOutsideClickDispatcher) { }

  open({ x, y }: MouseEvent, node: any, openContextMenuonLeftClick?: boolean) {
    let count = 0;
    this.contextMenuTemplate = node?.isHeaderContextMenu ? this.headerContextMenu : node.isCollapsibleContextMenu ? this.collapsedContextMenu : this.contextMenu;

    if (node && (node.contextMenuOptions || node.getContextMenu)) {
      this.contextMenuOptions = node.getContextMenu ? node.getContextMenu(node.data) : node.contextMenuOptions;
    } else {
      this.contextMenuOptions = node;
    }

    if (this.contextMenuTemplate === this.headerContextMenu || this.contextMenuTemplate === this.contextMenu) {
      this.contextMenuOptions = this.hideNonVisibleOptions(this.contextMenuOptions, node);
      this.contextMenuOptions = this.enableDisableOptions(this.contextMenuOptions, node);
    }

    if (this.overlayOutsideClickDispatcher._attachedOverlays.length > 0) {
      this.overlayOutsideClickDispatcher._attachedOverlays[0].dispose();
      this.close(true);
    }

    this.close();

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(node.parentElCoordinates ? node.parentElCoordinates : { x, y })
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
        }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: 'context-menu-panel',
      hasBackdrop: false
    });

    const overlayEl = this.overlayRef.overlayElement;
    overlayEl.style.visibility = 'hidden'; // Prevent flash in top-left

    this.overlayRef.attach(new TemplatePortal(this.contextMenuTemplate, this.viewContainerRef, {
      $implicit: node
    }));

    requestAnimationFrame(() => {
      this.overlayRef.updatePosition();
      overlayEl.style.visibility = 'visible';
    });

    setTimeout(() => {
      // check for bad positioning of the overlay and update positions accordingly
      this.updatePositionofOverlay();
    })

    this.sub = fromEvent<MouseEvent>(document, 'click')
      .pipe(
        filter(event => {
          const clickTarget = event.target as HTMLElement;
          if (openContextMenuonLeftClick) {
            count++;
            openContextMenuonLeftClick = count === 1 ? true : false;
          }
          return !!this.overlayRef && !this.overlayRef.overlayElement?.contains(clickTarget) && (!openContextMenuonLeftClick || this.overlayOutsideClickDispatcher._attachedOverlays.length > 0);
        }),
        take(1)
      ).subscribe(() => {
        this.close(true);
      })
  }

  close(isContextMenuOpen?: boolean) {
    this.sub && this.sub.unsubscribe();

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }

    if (this.closeContextMenuEvent && isContextMenuOpen) {
      this.closeContextMenuEvent();
    }
  }

  closeAllContextMenus(): void {
    if (this.overlayOutsideClickDispatcher._attachedOverlays.length > 0) {
      this.overlayOutsideClickDispatcher._attachedOverlays.forEach(attachedOverlay => {
        attachedOverlay.dispose();
      });
    }
  }

  onOptionClicked(event, option, user) {
    if (option.enabled) {
      option.click(user);
      if (!(this.contextMenuTemplate === this.headerContextMenu)) {
        this.close(true);
      }
    }
  }

  hideNonVisibleOptions(contextMenuOptions, node): Array<ContextMenuOption> {
    const filteredCotextMenus = [];
    if (contextMenuOptions?.length > 0) {
      contextMenuOptions?.forEach(contextMenuOption => {
        if (contextMenuOption.isVisible(node) || (contextMenuOption.visible && node.contextMenuType === 'workflows')) {
          filteredCotextMenus.push(contextMenuOption)
        }
      });
    }
    return filteredCotextMenus;
  }

  enableDisableOptions(contextMenuOptions, node): Array<ContextMenuOption> {
    const enabledDisabledContextMenus = [];
    contextMenuOptions?.forEach(contextMenuOption => {
      if (typeof contextMenuOption.enabled === 'function') {
        contextMenuOption.enabledFunction = contextMenuOption.enabled;
        contextMenuOption.enabled = contextMenuOption.enabled(node);
      } else {
        if (typeof contextMenuOption.enabledFunction === 'function') {
          contextMenuOption.enabled = contextMenuOption.enabledFunction(node);
        }
      }
      enabledDisabledContextMenus.push(contextMenuOption);
    })
    return enabledDisabledContextMenus;
  }

  onModelChange(option) {
    if (this.onModelChangeEvent) {
      this.onModelChangeEvent(option);
    }
  }

  deselectAll(event) {
    if (this.deselectAllEvent) {
      this.deselectAllEvent(event);
    }
  }

  updatePositionofOverlay(): void {
    const overlayEl = this.overlayRef.overlayElement,
      clientHeight = overlayEl.children[0].children[0].clientHeight,
      clientWidth = overlayEl.children[0].children[0].clientWidth,
      offsetTop = overlayEl.parentElement.offsetTop,
      offsetWidth = overlayEl.parentElement.offsetWidth,
      offsetHeight = overlayEl.parentElement.offsetHeight;

    if (offsetWidth + clientWidth > window.innerWidth) {
      (<HTMLElement>overlayEl.children[0].children[0]).style.left = -clientWidth + 'px';
    }

    if (clientHeight > offsetHeight && clientHeight < offsetTop) {
      (<HTMLElement>overlayEl.children[0].children[0]).style.top = -clientHeight + 'px';
    }

    if (clientHeight > offsetHeight && clientHeight > offsetTop) {
      const bottom = overlayEl.children[0].children[0].getBoundingClientRect().bottom;
      (<HTMLElement>overlayEl.children[0].children[0]).style.top = -clientHeight + (clientHeight / 2) + 'px';

      if (overlayEl.children[0].children[0].getBoundingClientRect().top < 0) {
        (<HTMLElement>overlayEl.children[0].children[0]).style.top = -clientHeight - overlayEl.children[0].children[0].getBoundingClientRect().top + (clientHeight / 2) + 'px';
      }

      else if (overlayEl.children[0].children[0].getBoundingClientRect().bottom > document.documentElement.scrollHeight) {
        (<HTMLElement>overlayEl.children[0].children[0]).style.top = (document.documentElement.scrollHeight - bottom) + 'px';
      }
    }
  }

}
